import React, { useState } from "react";
import { useParams, useNavigate, useOutletContext } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import baseURL from "../utils/BaseURL";

export const getUnitLabel = (type) => {
  if (!type) return "Case";
  const map = {
    single: "Ea",
    pack: "Pack",
    case: "Case",
    mastercase: "Mater case",
  };
  const key = String(type).toLowerCase();
  return map[key] || type.charAt(0).toUpperCase() + type.slice(1);
};

// API Fetchers
const fetchProductDetails = async (id) => {
  const { data } = await axios.get(`${baseURL}/product/${id}`);
  if (!data.success) throw new Error("Failed to fetch product details");
  return data.result;
};

// API Fetchers
const fetchProductCaseDetails = async (id) => {
  const { data } = await axios.get(`${baseURL}/product_case/${id}`);
  if (!data.success) throw new Error("Failed to fetch product case details");
  return data.result;
};

const fetchProductReviews = async (id, page = 1) => {
  const { data } = await axios.get(`${baseURL}/reviews/${id}?page=${page}`);
  if (!data.success) throw new Error("Failed to fetch product reviews");
  return data.result;
};

const ProductDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const outletData = useOutletContext?.();
  const productId = id || 24; // Fallback for testing

  // States
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState("description");
  const [reviewPage, setReviewPage] = useState(1);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [showPlatformModal, setShowPlatformModal] = useState(false);

  // Queries
  const {
    data: product,
    isLoading: productLoading,
    isError: productError,
  } = useQuery({
    queryKey: ["product", productId],
    queryFn: () => fetchProductDetails(productId),
    staleTime: 1000 * 60 * 5,
    enabled: !!productId,
  });
  // Queries
  const {
    data: productCaseDetails,
    isLoading: productCaseLoading,
    isError: productCaseError,
  } = useQuery({
    queryKey: ["product_case", productId],
    queryFn: () => fetchProductCaseDetails(productId),
    staleTime: 1000 * 60 * 5,
    enabled: !!productId,
  });

  const { data: reviewsData, isLoading: reviewsLoading } = useQuery({
    queryKey: ["reviews", productId, reviewPage],
    queryFn: () => fetchProductReviews(productId, reviewPage),
    enabled: !!productId,
  });

  const { isError: reviewsError } = useQuery({
    queryKey: ["reviews", productId, reviewPage, "errorCheck"],
    queryFn: () => fetchProductReviews(productId, reviewPage),
    enabled: false,
  });

  // Unified loading state for all queries
  if (productLoading || productCaseLoading || reviewsLoading) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <div className="w-12 h-12 border-4 border-[#998147] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Unified error state for any query failure
  if (
    productError ||
    productCaseError ||
    reviewsError ||
    !product ||
    !productCaseDetails
  ) {
    return (
      <div className="min-h-screen flex justify-center items-center text-red-500 font-medium text-lg">
        Oops! Failed to load product details.
      </div>
    );
  }

  const formatPrice = (price) => parseFloat(price).toFixed(2);
  const totalReviews = reviewsData?.ratings?.total_review || 0;

  // Calculate average rating
  const ratings = reviewsData?.ratings || {};
  const averageRating =
    totalReviews > 0
      ? (ratings.poor * 1 +
          ratings.average * 3 +
          ratings.very_good * 4 +
          ratings.excellent * 5) /
        totalReviews
      : 0;

  const casePriceValue = parseFloat(
    // Prefer new API top-level `price` (from product_case), fall back to existing case price fields
    product.price ||
      product.case_price?.discounted_price ||
      product.casePrice?.discounted_price ||
      product.case_price?.price ||
      product.casePrice?.price ||
      0,
  );
  const caseSize = parseFloat(product.case_size || product.pack_size || 1);
  const unitPriceValue = parseFloat(
    product.case_price?.unit_price ||
      product.casePrice?.unit_price ||
      (caseSize > 0 ? casePriceValue / caseSize : 0) ||
      0,
  );

  const caseLabel = getUnitLabel(productCaseDetails?.unit_type);

  // Star Rating Helper
  const renderStars = (ratingStr, size = "w-5 h-5") => {
    const rating = parseFloat(ratingStr) || 0;
    return (
      <div className="flex text-[#FBBF24]">
        {[1, 2, 3, 4, 5].map((star) => (
          <svg
            key={star}
            className={`${size} ${star <= Math.round(rating) ? "fill-current" : "text-gray-300 fill-current"}`}
            viewBox="0 0 20 20"
          >
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
      </div>
    );
  };

  // App store links (prefer links from landing data outlet, else fallback)
  const ANDROID_LINK =
    outletData?.appSection?.googlePlayLink ||
    "https://play.google.com/store/apps/details?id=com.example.app";
  const IOS_LINK =
    outletData?.appSection?.appStoreLink ||
    "https://apps.apple.com/app/id000000000";

  const isMobileDevice = () => {
    if (typeof navigator === "undefined") return false;
    return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent || "");
  };

  const detectMobileOS = () => {
    if (typeof navigator === "undefined") return null;
    const ua = navigator.userAgent || "";
    if (/android/i.test(ua)) return "android";
    if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
    return null;
  };

  const redirectToStore = (platform) => {
    if (platform === "android") {
      window.location.href = ANDROID_LINK;
    } else if (platform === "ios") {
      window.location.href = IOS_LINK;
    }
  };

  const handleBuyNow = () => {
    const os = detectMobileOS();
    if (isMobileDevice() && os) {
      // Directly redirect on mobile
      redirectToStore(os);
    } else {
      // Show modal on desktop or unknown mobile
      setShowPlatformModal(true);
    }
  };

  const handleChoosePlatform = (platform) => {
    setShowPlatformModal(false);
    redirectToStore(platform);
  };

  return (
    <div className="md:px-24 px-3 mt-32 w-full bg-white pb-20">
      <div className=" mx-auto px-5 md:px-10 pt-10">
        {/* ================= TOP: PRODUCT INFO ================= */}
        <div className="flex flex-col lg:flex-row gap-10 lg:gap-14 mb-16 lg:h-[40rem] h-auto">
          {/* Left: Product Image */}
          <div className="w-full lg:w-[50%] relative rounded-3xl">
            <div className="relative flex justify-center w-full items-center bg-white rounded-2xl overflow-hidden aspect-square lg:h-[33rem] h-[24rem]">
              <img
                src={
                  product.image?.[selectedImageIndex] ||
                  product.image?.[0] ||
                  ""
                }
                alt={product.name}
                className="object-contain w-full h-full px-10 bg-[#F9F8F5]"
              />
            </div>
            <div className="mt-4 grid grid-cols-4 gap-2">
              {(product.image || []).map((imgUrl, idx) => (
                <button
                  key={`${imgUrl}-${idx}`}
                  onClick={() => setSelectedImageIndex(idx)}
                  className={`rounded-xl overflow-hidden border transition-all duration-200 focus:outline-none ${
                    selectedImageIndex === idx
                      ? "border-2 border-[#998147] shadow-lg"
                      : "border border-gray-200"
                  }`}
                >
                  <img
                    src={imgUrl}
                    alt={`${product.name} ${idx + 1}`}
                    className="h-32 w-full object-contain p-3 bg-[#F9F8F5]"
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Right: Product Details */}
          <div className="w-full lg:w-[50%] flex flex-col justify-center">
            <h1 className="text-3xl md:text-5xl font-extrabold text-[#15363A] leading-tight mb-3">
              {product.name} {product.specification}
            </h1>
            <p className="text-gray-600 text-lg mb-5">
              {product.short_description ||
                "Premium formula, trusted quality, fast-acting."}
            </p>

            <div className="flex flex-wrap items-center gap-3 mb-5">
              <div className="flex items-center gap-1">
                {renderStars(product.rate, "w-5 h-5")}
              </div>
              <span className="text-2xl font-bold text-[#998147]">
                {product.rate || "0.0"}
              </span>
              <span className="text-base text-gray-500">
                ({totalReviews || 0} reviews)
              </span>
            </div>

            <div className="mb-6">
              <div className="flex items-end gap-3 flex-wrap">
                <div className="flex items-baseline gap-1">
                  <span className="text-5xl md:text-5xl font-semibold text-[#998147]">
                    ${formatPrice(productCaseDetails.price)}
                  </span>
                  <span className="text-gray-500 font-medium md:text-xl">
                    /{caseLabel}
                  </span>
                </div>
                <span className="text-lg md:text-2xl font-semibold text-gray-500">
                  ${formatPrice(unitPriceValue)}/Ea
                </span>
              </div>
            </div>

            <div className="md:mb-8 md:mt-0 mt-6">
              <p className="mb-2 text-sm text-gray-500 uppercase tracking-wider font-semibold">
                Case options
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {(product.same_name_products?.slice(0, 3) || []).map(
                  (variant) => (
                    <button
                      key={variant.id}
                      onClick={() => navigate(`/product/${variant.id}/details`)}
                      className="border border-gray-200 rounded-xl p-3 bg-white hover:border-[#998147] transition-shadow shadow-sm hover:shadow-md text-left"
                    >
                      <img
                        src={variant.image}
                        alt={variant.name}
                        className="mx-auto mb-2 h-16 w-16 object-contain"
                      />
                      <div className="text-sm font-semibold text-[#15363A]">
                        {variant.specification || variant.name}
                      </div>
                      <div className="text-xs text-gray-500">
                        x{variant.case_size || 1}
                      </div>
                      <div className="text-sm font-bold text-[#998147]">
                        $
                        {formatPrice(
                          // Prefer variant.price/unit_type from new API when available
                          variant.price ||
                            variant.case_price ||
                            variant.casePrice?.discounted_price ||
                            variant.case_price?.discounted_price ||
                            0,
                        )}{" "}
                        /{variant.unit_type || "case"}
                      </div>
                    </button>
                  ),
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8">
              {product.deliveryDays && (
                <div className="flex items-center gap-3 border border-gray-200 rounded-2xl px-4 py-3 md:mt-0 mt-5">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 text-[#998147]"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M12 1L2 5l10 4 10-4-10-4z" />
                    <path d="M2 15l10 4 10-4" />
                    <path d="M2 11l10 4 10-4" />
                  </svg>
                  <span className="text-sm font-semibold text-[#15363A] ">
                    {product.deliveryDays} Day Delivery
                  </span>
                </div>
              )}
              {product.returnDays !== null && (
                <div className="flex items-center gap-3 border border-gray-200 rounded-2xl px-4 py-3">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 text-[#998147]"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v6h6M20 20v-6h-6M20 8a12 12 0 11-16 0 12 12 0 0116 0z"
                    />
                  </svg>
                  <span className="text-sm font-semibold text-[#15363A]">
                    {product.returnDays} Day Returns
                  </span>
                </div>
              )}
            </div>

            <div className=" flex items-center gap-3">
              <button
                onClick={handleBuyNow}
                className="w-full  md:w-1/2 py-5 bg-[#998147] text-white font-bold rounded-xl hover:bg-opacity-90 transition"
              >
                Buy Now
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ================= TAB + REVIEWS SPLIT LAYOUT ================= */}
      <div className="mb-20 flex flex-col lg:flex-row gap-8">
        {/* Left: tabs content (3/4) */}
        <div className="w-full lg:w-3/4 bg-white rounded-3xl border border-gray-200 p-6 shadow-sm">
          {/* Tab Headers */}
          <div className="flex items-center gap-8 border-b border-gray-200 ">
            {["description", "delivery", "details"].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`pb-4 text-lg font-bold whitespace-nowrap transition-colors relative ${
                  activeTab === tab
                    ? "text-[#15363A]"
                    : "text-gray-400 hover:text-gray-600"
                }`}
              >
                {tab === "description" && "Product Description"}
                {tab === "details" && "Details"}
                {activeTab === tab && (
                  <div className="absolute bottom-[-1px] left-0 w-full h-[3px] bg-[#15363A] rounded-t-full"></div>
                )}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="pt-8 text-gray-600 text-lg leading-relaxed">
            {activeTab === "description" && (
              <div>
                <p className="mb-6">{product.description}</p>
                {product.keyFeatures?.length > 0 && (
                  <ul className="space-y-3">
                    {product.keyFeatures.map((feature, idx) => (
                      <li
                        key={idx}
                        className="flex items-center gap-3 font-medium"
                      >
                        <span className="w-2 h-2 rounded-full bg-[#998147]"></span>
                        {feature.value}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {activeTab === "delivery" && (
              <div className="space-y-4">
                <div className="flex items-center gap-3 font-medium text-[#15363A]">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 text-[#998147]"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
                    />
                  </svg>
                  Estimated Delivery:{" "}
                  <span className="text-gray-600">
                    {product.deliveryDays || 3} Days
                  </span>
                </div>
                <div className="flex items-center gap-3 font-medium text-[#15363A]">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 text-[#998147]"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 15v-1a4 4 0 00-4-4H8m0 0l3 3m-3-3l3-3m9 14V5a2 2 0 00-2-2H6a2 2 0 00-2 2v16l4-2 4 2 4-2 4 2z"
                    />
                  </svg>
                  Return Policy:{" "}
                  <span className="text-gray-600">
                    {product.returnDays || 30} Days
                  </span>
                </div>
              </div>
            )}

            {activeTab === "details" && (
              <div className="grid grid-cols-1 md:grid-cols-1 gap-4 max-w-2xl">
                {product.details?.map((detail, idx) => (
                  <div
                    key={idx}
                    className="flex justify-between py-3 border-b border-gray-100"
                  >
                    <span className="font-bold text-[#15363A]">
                      {detail.key}
                    </span>
                    <span className="text-gray-500">{detail.value}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right: compact customer reviews (1/4) */}
        <aside className="w-full lg:w-1/4 bg-white rounded-3xl border border-gray-200 p-6 shadow-sm">
          <h2 className="text-2xl font-bold text-[#15363A] mb-4">
            Customer Reviews
          </h2>
          <div className="mb-5">
            <p className="text-5xl font-extrabold text-[#998147] leading-none">
              {averageRating.toFixed(1)}
            </p>
            <div className="flex items-center gap-2 mt-1">
              {renderStars(averageRating, "w-5 h-5")}
            </div>
            <p className="text-gray-500 text-sm">
              Based on {totalReviews} ratings
            </p>
          </div>

          <div className="space-y-3">
            {(reviewsData?.reviews || []).map((review, idx) => (
              <div key={idx} className="border-b  last:border-none pb-4 ">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-sm font-semibold text-[#15363A]">
                    {review.name}
                  </h3>
                  <span className="text-xs text-gray-400">{review.time}</span>
                </div>
                <div className="flex items-center gap-1 mb-2">
                  {renderStars(review.rating, "w-4 h-4")}
                </div>
                <p className="text-sm text-gray-600">"{review.text}"</p>
              </div>
            ))}
          </div>

          {/* <button className="mt-6 w-full px-4 py-3 border border-[#15363A] text-[#15363A] font-bold rounded-xl hover:bg-[#15363A] hover:text-white transition-colors">
            Read All Reviews
          </button> */}
        </aside>
      </div>

      {showPlatformModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black opacity-40"
            onClick={() => setShowPlatformModal(false)}
          />
          <div className="bg-white rounded-xl p-6 z-10 w-full max-w-sm shadow-lg">
            <h3 className="text-lg font-bold text-[#15363A] mb-2">
              Download App
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Choose your platform to download the app.
            </p>
            <div className="flex gap-3">
              <a
                href={ANDROID_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 px-4 py-3 bg-[#998147] text-white rounded-lg font-semibold text-center"
              >
                Android
              </a>
              <a
                href={IOS_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 px-4 py-3 border border-gray-200 rounded-lg font-semibold text-center"
              >
                iOS
              </a>
            </div>
            <button
              onClick={() => setShowPlatformModal(false)}
              className="mt-4 w-full text-sm text-gray-600"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetails;
